import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Calendar, Clock, Settings, FileText, Plus, Search, Filter, Edit, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { timeManagementAPI } from '../../../services/api';
import { toast } from 'sonner';

const TimeManagementConfig = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('config');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('holiday-calendars');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const tabs = [
    { id: 'holiday-calendars', label: 'Holiday Calendars', icon: Calendar },
    { id: 'daily-work-schedules', label: 'Daily Work Schedules', icon: Clock },
    { id: 'work-schedule-rules', label: 'Work Schedule Rules', icon: Settings },
    { id: 'absence-types', label: 'Absence Types', icon: FileText },
    { id: 'attendance-types', label: 'Attendance Types', icon: FileText },
    { id: 'absence-quota-types', label: 'Quota Types', icon: FileText },
  ];


  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (activeTab) {
        case 'holiday-calendars':
          response = await timeManagementAPI.getHolidayCalendars();
          break;
        case 'daily-work-schedules':
          response = await timeManagementAPI.getDailyWorkSchedules();
          break;
        case 'work-schedule-rules':
          response = await timeManagementAPI.getWorkScheduleRules();
          break;
        case 'absence-types':
          response = await timeManagementAPI.getAbsenceTypes();
          break;
        case 'attendance-types':
          response = await timeManagementAPI.getAttendanceTypes();
          break;
        case 'absence-quota-types':
          response = await timeManagementAPI.getAbsenceQuotaTypes();
          break;
        default:
          response = { data: [] };
      }
      setData(response.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredData = data.filter(item => {
    const name = item.name || '';
    const id = item.holiday_calendar_id || item.dws_id || item.work_schedule_rule_id || 
               item.absence_type_id || item.attendance_type_id || item.quota_type_id || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase()) || 
           id.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getIdField = () => {
    switch (activeTab) {
      case 'holiday-calendars': return 'holiday_calendar_id';
      case 'daily-work-schedules': return 'dws_id';
      case 'work-schedule-rules': return 'work_schedule_rule_id';
      case 'absence-types': return 'absence_type_id';
      case 'attendance-types': return 'attendance_type_id';
      case 'absence-quota-types': return 'quota_type_id';
      default: return 'id';
    }
  };

  const renderTable = () => {
    const idField = getIdField();
    
    switch (activeTab) {
      case 'holiday-calendars':
        return (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Country</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Year</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Holidays</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={item[idField]} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item[idField]}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.country_code}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.year}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.holiday_count || item.holidays?.length || 0}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3">
                    <RecordActions doctype="config" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      
      case 'daily-work-schedules':
        return (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Day Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Work Hours</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Time</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={item[idField]} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item[idField]}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <DayTypeBadge dayType={item.day_type} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.planned_hours}h</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {item.work_start && item.work_end ? `${item.work_start} - ${item.work_end}` : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3">
                    <RecordActions doctype="config" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'work-schedule-rules':
        return (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Holiday Calendar</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Weekly Hours</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={item[idField]} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item[idField]}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.schedule_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.holiday_calendar_name || item.holiday_calendar_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.planned_hours_per_week}h</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3">
                    <RecordActions doctype="config" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'absence-types':
        return (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Paid</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Quota Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Approval</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={item[idField]} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item[idField]}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <CategoryBadge category={item.category} type="absence" />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {item.is_paid ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-gray-400" />}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.quota_type_name || item.quota_type_id || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    {item.requires_approval ? <CheckCircle className="h-4 w-4 text-orange-500" /> : <XCircle className="h-4 w-4 text-gray-400" />}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3">
                    <RecordActions doctype="config" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'attendance-types':
        return (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Overtime</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">OT Rate</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Approval</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={item[idField]} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item[idField]}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <CategoryBadge category={item.category} type="attendance" />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {item.is_overtime ? <CheckCircle className="h-4 w-4 text-orange-500" /> : <XCircle className="h-4 w-4 text-gray-400" />}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.overtime_rate ? `${item.overtime_rate}x` : '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    {item.requires_approval ? <CheckCircle className="h-4 w-4 text-orange-500" /> : <XCircle className="h-4 w-4 text-gray-400" />}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3">
                    <RecordActions doctype="config" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'absence-quota-types':
        return (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Unit</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Default Entitlement</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Accrual</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Carryover</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={item[idField]} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item[idField]}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.unit}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.default_entitlement}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.accrual_method}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.max_carryover || '-'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3">
                    <RecordActions doctype="config" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      default:
        return null;
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="time-config-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/time')}>Time Management</span>
            <span>/</span>
            <span>Configuration</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Time Management Configuration</h1>
              <p className="text-gray-600 mt-1">Configure work schedules, holidays, and absence types</p>
            </div>
            {writable && (<Button onClick={handleCreate} data-testid="btn-create">
              <Plus className="h-4 w-4 mr-2" />
              Create New
            </Button>)}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'bg-orange-100 text-orange-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredData.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No data found. Click "Create New" to add a record.
            </div>
          ) : (
            <div className="overflow-x-auto">
              {renderTable()}
            </div>
          )}
        </Card>

        {/* Form Modal would go here */}
        {showForm && (
          <ConfigFormModal 
            activeTab={activeTab}
            editingItem={editingItem}
            onClose={() => { setShowForm(false); setEditingItem(null); }}
            onSave={() => { setShowForm(false); setEditingItem(null); loadData(); }}
          />
        )}
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

// Helper Components
const StatusBadge = ({ status }) => {
  const colors = {
    'Active': 'bg-green-100 text-green-700',
    'Blocked': 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

const DayTypeBadge = ({ dayType }) => {
  const config = {
    '0': { label: 'Work Day', color: 'bg-green-100 text-green-700' },
    '1': { label: 'Day Off', color: 'bg-gray-100 text-gray-700' },
    '2': { label: 'Special', color: 'bg-orange-100 text-orange-700' },
  };
  const { label, color } = config[dayType] || { label: dayType, color: 'bg-gray-100 text-gray-700' };
  return <span className={`px-2 py-1 text-xs font-medium rounded-full ${color}`}>{label}</span>;
};

const CategoryBadge = ({ category, type }) => {
  const absenceColors = {
    'VACATION': 'bg-green-100 text-green-700',
    'SICK': 'bg-red-100 text-red-700',
    'UNPAID': 'bg-gray-100 text-gray-700',
    'MATERNITY': 'bg-pink-100 text-pink-700',
    'PATERNITY': 'bg-orange-100 text-orange-700',
    'TRAINING': 'bg-orange-100 text-orange-700',
    'OTHER': 'bg-gray-100 text-gray-700',
  };
  const attendanceColors = {
    'OVERTIME': 'bg-orange-100 text-orange-700',
    'BUSINESS_TRIP': 'bg-orange-100 text-orange-700',
    'TRAINING': 'bg-orange-100 text-orange-700',
    'OTHER': 'bg-gray-100 text-gray-700',
  };
  const colors = type === 'absence' ? absenceColors : attendanceColors;
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[category] || 'bg-gray-100 text-gray-700'}`}>
      {category}
    </span>
  );
};

// Simple Form Modal Component
const ConfigFormModal = ({ activeTab, editingItem, onClose, onSave }) => {
  const [formData, setFormData] = useState(editingItem || {});
  const [loading, setLoading] = useState(false);
  const [holidays, setHolidays] = useState(editingItem?.holidays || []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let updateData = {};
      
      switch (activeTab) {
        case 'holiday-calendars':
          updateData = {
            name: formData.name,
            description: formData.description,
            country_code: formData.country_code,
            valid_to: formData.valid_to,
            holidays: holidays,
            status: formData.status
          };
          if (editingItem) {
            await timeManagementAPI.updateHolidayCalendar(editingItem.holiday_calendar_id, updateData);
          } else {
            await timeManagementAPI.createHolidayCalendar({
              ...updateData,
              holiday_calendar_id: formData.holiday_calendar_id,
              year: parseInt(formData.year),
              valid_from: formData.valid_from
            });
          }
          break;
        case 'daily-work-schedules':
          updateData = {
            name: formData.name,
            description: formData.description,
            day_type: formData.day_type,
            planned_hours: parseFloat(formData.planned_hours) || 8,
            work_start: formData.work_start,
            work_end: formData.work_end,
            status: formData.status
          };
          if (editingItem) {
            await timeManagementAPI.updateDailyWorkSchedule(editingItem.dws_id, updateData);
          }
          break;
        case 'absence-types':
          updateData = {
            name: formData.name,
            description: formData.description,
            category: formData.category,
            is_paid: formData.is_paid,
            counting_rule: formData.counting_rule,
            requires_approval: formData.requires_approval,
            status: formData.status
          };
          if (editingItem) {
            await timeManagementAPI.updateAbsenceType(editingItem.absence_type_id, updateData);
          }
          break;
        case 'work-schedule-rules':
          updateData = {
            name: formData.name,
            description: formData.description,
            holiday_calendar_id: formData.holiday_calendar_id || null,
            weekly_hours: parseFloat(formData.weekly_hours) || 40,
            status: formData.status
          };
          if (editingItem) {
            await timeManagementAPI.updateWorkScheduleRule(editingItem.work_schedule_rule_id, updateData);
          }
          break;
        case 'attendance-types':
          updateData = {
            name: formData.name,
            description: formData.description,
            category: formData.category,
            is_overtime: formData.is_overtime ?? false,
            overtime_multiplier: parseFloat(formData.overtime_multiplier) || 1.0,
            requires_approval: formData.requires_approval ?? false,
            status: formData.status
          };
          if (editingItem) {
            await timeManagementAPI.updateAttendanceType(editingItem.attendance_type_id, updateData);
          }
          break;
        case 'absence-quota-types':
          updateData = {
            name: formData.name,
            description: formData.description,
            absence_type_id: formData.absence_type_id || null,
            accrual_frequency: formData.accrual_frequency,
            default_quota: parseFloat(formData.default_quota) || 0,
            max_carryover: parseFloat(formData.max_carryover) || 0,
            status: formData.status
          };
          if (editingItem) {
            await timeManagementAPI.updateAbsenceQuotaType(editingItem.quota_type_id, updateData);
          }
          break;
        default:
          toast.error('Form not implemented for this type');
          setLoading(false);
          return;
      }
      
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
      onSave();
    } catch (error) {
      console.error('Operation failed:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const addHoliday = () => {
    setHolidays([...holidays, { holiday_date: '', name: '', holiday_type: 'Public', is_half_day: false }]);
  };

  const removeHoliday = (index) => {
    setHolidays(holidays.filter((_, i) => i !== index));
  };

  const updateHoliday = (index, field, value) => {
    const updated = [...holidays];
    updated[index] = { ...updated[index], [field]: value };
    setHolidays(updated);
  };

  const renderFormFields = () => {
    switch (activeTab) {
      case 'holiday-calendars':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Calendar ID *</label>
                <Input
                  value={formData.holiday_calendar_id || ''}
                  onChange={(e) => setFormData({...formData, holiday_calendar_id: e.target.value})}
                  disabled={!!editingItem}
                  placeholder="e.g., US2025"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., US Holidays 2025"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country Code *</label>
                <Input
                  value={formData.country_code || ''}
                  onChange={(e) => setFormData({...formData, country_code: e.target.value.toUpperCase()})}
                  placeholder="US"
                  maxLength={3}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                <Input
                  type="number"
                  value={formData.year || ''}
                  onChange={(e) => setFormData({...formData, year: e.target.value})}
                  placeholder="2025"
                  min={2000}
                  max={2100}
                  disabled={!!editingItem}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status || 'Active'}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Active">Active</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
            </div>
            
            {/* Holidays Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-gray-900">Holidays ({holidays.length})</h3>
                {writable && (<Button type="button" size="sm" variant="outline" onClick={addHoliday}>
                  <Plus className="h-4 w-4 mr-1" /> Add Holiday
                </Button>)}
              </div>
              {holidays.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No holidays defined. Click "Add Holiday" to add one.</p>
              ) : (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {holidays.map((holiday, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <Input
                          type="date"
                          value={holiday.holiday_date?.split('T')[0] || ''}
                          onChange={(e) => updateHoliday(index, 'holiday_date', e.target.value)}
                          className="mb-2"
                          placeholder="Date"
                        />
                        <Input
                          value={holiday.name || ''}
                          onChange={(e) => updateHoliday(index, 'name', e.target.value)}
                          placeholder="Holiday name"
                        />
                      </div>
                      <div className="w-28">
                        <select
                          value={holiday.holiday_type || 'Public'}
                          onChange={(e) => updateHoliday(index, 'holiday_type', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                        >
                          <option value="Public">Public</option>
                          <option value="Religious">Religious</option>
                          <option value="Company">Company</option>
                          <option value="Regional">Regional</option>
                        </select>
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={holiday.is_half_day || false}
                            onChange={(e) => updateHoliday(index, 'is_half_day', e.target.checked)}
                            className="mr-2"
                          />
                          Half Day
                        </label>
                      </div>
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeHoliday(index)} className="text-red-500">
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'daily-work-schedules':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">DWS ID *</label>
                <Input
                  value={formData.dws_id || ''}
                  onChange={(e) => setFormData({...formData, dws_id: e.target.value})}
                  disabled={!!editingItem}
                  placeholder="e.g., DWS-STD8"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Standard 8-Hour Workday"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day Type *</label>
                <select
                  value={formData.day_type || '0'}
                  onChange={(e) => setFormData({...formData, day_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="0">Work Day</option>
                  <option value="1">Day Off</option>
                  <option value="2">Special</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Planned Hours</label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.planned_hours || 8}
                  onChange={(e) => setFormData({...formData, planned_hours: e.target.value})}
                  min={0}
                  max={24}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status || 'Active'}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Active">Active</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Start</label>
                <Input
                  type="time"
                  value={formData.work_start || ''}
                  onChange={(e) => setFormData({...formData, work_start: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work End</label>
                <Input
                  type="time"
                  value={formData.work_end || ''}
                  onChange={(e) => setFormData({...formData, work_end: e.target.value})}
                />
              </div>
            </div>
          </div>
        );

      case 'absence-types':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Absence Type ID *</label>
                <Input
                  value={formData.absence_type_id || ''}
                  onChange={(e) => setFormData({...formData, absence_type_id: e.target.value})}
                  disabled={!!editingItem}
                  placeholder="e.g., VACATION"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Annual Vacation"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  value={formData.category || 'OTHER'}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="VACATION">Vacation</option>
                  <option value="SICK">Sick Leave</option>
                  <option value="UNPAID">Unpaid Leave</option>
                  <option value="MATERNITY">Maternity</option>
                  <option value="PATERNITY">Paternity</option>
                  <option value="TRAINING">Training</option>
                  <option value="COMPENSATORY">Compensatory</option>
                  <option value="BEREAVEMENT">Bereavement</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Counting Rule</label>
                <select
                  value={formData.counting_rule || 'WORK_DAYS'}
                  onChange={(e) => setFormData({...formData, counting_rule: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="WORK_DAYS">Work Days (excl. weekends/holidays)</option>
                  <option value="CALENDAR">Calendar Days</option>
                  <option value="HOURS">Hours</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status || 'Active'}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Active">Active</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_paid"
                  checked={formData.is_paid ?? true}
                  onChange={(e) => setFormData({...formData, is_paid: e.target.checked})}
                  className="mr-2"
                />
                <label htmlFor="is_paid" className="text-sm text-gray-700">Paid Leave</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requires_approval"
                  checked={formData.requires_approval ?? true}
                  onChange={(e) => setFormData({...formData, requires_approval: e.target.checked})}
                  className="mr-2"
                />
                <label htmlFor="requires_approval" className="text-sm text-gray-700">Requires Approval</label>
              </div>
            </div>
          </div>
        );

      case 'work-schedule-rules':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rule ID *</label>
                <Input
                  value={formData.work_schedule_rule_id || ''}
                  onChange={(e) => setFormData({...formData, work_schedule_rule_id: e.target.value})}
                  disabled={!!editingItem}
                  placeholder="e.g., WSR-STD40"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Standard 40-Hour Week"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Hours</label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.weekly_hours || 40}
                  onChange={(e) => setFormData({...formData, weekly_hours: e.target.value})}
                  min={0}
                  max={168}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Calendar</label>
                <Input
                  value={formData.holiday_calendar_id || ''}
                  onChange={(e) => setFormData({...formData, holiday_calendar_id: e.target.value})}
                  placeholder="e.g., US2025"
                />
                <p className="text-xs text-gray-500 mt-1">Calendar ID for public holidays</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status || 'Active'}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Active">Active</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
            </div>
            <div className="border-t pt-4 mt-2">
              <h4 className="font-medium mb-3 text-sm text-gray-700">Weekly Pattern</h4>
              <p className="text-xs text-gray-500 mb-3">Daily Work Schedule IDs for each day of the week</p>
              <div className="grid grid-cols-7 gap-2">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                  <div key={day}>
                    <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">{day.slice(0, 3)}</label>
                    <Input
                      value={formData.weekly_pattern?.[`${day}_dws_id`] || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        weekly_pattern: {
                          ...formData.weekly_pattern,
                          [`${day}_dws_id`]: e.target.value
                        }
                      })}
                      placeholder="DWS ID"
                      className="text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'attendance-types':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type ID *</label>
                <Input
                  value={formData.attendance_type_id || ''}
                  onChange={(e) => setFormData({...formData, attendance_type_id: e.target.value})}
                  disabled={!!editingItem}
                  placeholder="e.g., REG_WORK"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Regular Working Hours"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category || 'REGULAR'}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="REGULAR">Regular</option>
                  <option value="OVERTIME">Overtime</option>
                  <option value="ON_CALL">On-Call</option>
                  <option value="TRAINING">Training</option>
                  <option value="TRAVEL">Travel</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Overtime Multiplier</label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.overtime_multiplier || 1.0}
                  onChange={(e) => setFormData({...formData, overtime_multiplier: e.target.value})}
                  min={0}
                  max={5}
                />
                <p className="text-xs text-gray-500 mt-1">e.g., 1.5 for time-and-a-half</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status || 'Active'}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Active">Active</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_overtime"
                  checked={formData.is_overtime ?? false}
                  onChange={(e) => setFormData({...formData, is_overtime: e.target.checked})}
                  className="mr-2"
                />
                <label htmlFor="is_overtime" className="text-sm text-gray-700">Is Overtime</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="att_requires_approval"
                  checked={formData.requires_approval ?? false}
                  onChange={(e) => setFormData({...formData, requires_approval: e.target.checked})}
                  className="mr-2"
                />
                <label htmlFor="att_requires_approval" className="text-sm text-gray-700">Requires Approval</label>
              </div>
            </div>
          </div>
        );

      case 'absence-quota-types':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quota Type ID *</label>
                <Input
                  value={formData.quota_type_id || ''}
                  onChange={(e) => setFormData({...formData, quota_type_id: e.target.value})}
                  disabled={!!editingItem}
                  placeholder="e.g., QUOTA_VAC"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Annual Vacation Quota"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Linked Absence Type</label>
                <Input
                  value={formData.absence_type_id || ''}
                  onChange={(e) => setFormData({...formData, absence_type_id: e.target.value})}
                  placeholder="e.g., VACATION"
                />
                <p className="text-xs text-gray-500 mt-1">Absence type that deducts from this quota</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Accrual Frequency</label>
                <select
                  value={formData.accrual_frequency || 'YEARLY'}
                  onChange={(e) => setFormData({...formData, accrual_frequency: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="YEARLY">Yearly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="NONE">No Accrual</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Quota (days)</label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.default_quota || 0}
                  onChange={(e) => setFormData({...formData, default_quota: e.target.value})}
                  min={0}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Carryover (days)</label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.max_carryover || 0}
                  onChange={(e) => setFormData({...formData, max_carryover: e.target.value})}
                  min={0}
                />
                <p className="text-xs text-gray-500 mt-1">Max days to carry to next period</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status || 'Active'}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Active">Active</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <p className="text-gray-500">
            Edit form for {activeTab} is not yet implemented.
          </p>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">
            {editingItem ? 'Edit' : 'Create'} {activeTab.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          {renderFormFields()}
          <div className="flex justify-end gap-3 pt-6 border-t mt-6">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimeManagementConfig;
